import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Scale, Plus, BellRing, Save } from "lucide-react";
import { toast } from "sonner";

const WEEKDAYS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];

export default function WeightTracker() {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [weight, setWeight] = useState("");
  const [saving, setSaving] = useState(false);
  const [rem, setRem] = useState({ reminder_enabled: true, reminder_weekday: 0, reminder_hour: 9 });
  const [savingRem, setSavingRem] = useState(false);

  const load = async () => {
    setLoading(true);
    const [w, r] = await Promise.all([api.get("/weight"), api.get("/reminder-settings")]);
    setLogs(w.data.logs || []);
    setRem(r.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const saveRem = async () => {
    setSavingRem(true);
    try {
      await api.put("/reminder-settings", rem);
      toast.success(rem.reminder_enabled
        ? `Promemoria impostato: ${WEEKDAYS[rem.reminder_weekday]} alle ${String(rem.reminder_hour).padStart(2, "0")}:00`
        : "Promemoria disattivato");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSavingRem(false); }
  };

  const add = async () => {
    if (!weight) { toast.error("Inserisci il peso"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/weight", { weight_kg: parseFloat(weight) });
      setLogs(data.logs || []);
      setWeight("");
      toast.success("Peso registrato");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (loading) return <Skeleton className="h-80 rounded-2xl" />;

  const chartData = logs.map((l) => ({ date: l.date?.slice(5), peso: l.weight_kg }));

  return (
    <div className="space-y-6" data-testid="weight-section">
      <div>
        <h2 className="font-heading text-2xl font-semibold">Monitoraggio del peso</h2>
        <p className="text-muted-foreground text-sm">Registra il peso una volta a settimana per seguire i progressi.</p>
      </div>

      <Card className="rounded-2xl">
        <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Scale className="h-5 w-5 text-primary" /> Nuova rilevazione</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-end gap-3 max-w-md">
            <div className="flex-1 space-y-2">
              <Label>Peso attuale (kg)</Label>
              <Input type="number" step="0.1" value={weight} onChange={(e) => setWeight(e.target.value)}
                placeholder="es. 78.5" data-testid="weight-input" />
            </div>
            <Button onClick={add} disabled={saving} data-testid="add-weight-button">
              <Plus className="h-4 w-4 mr-2" /> {saving ? "…" : "Registra"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader><CardTitle className="font-heading text-lg">Andamento</CardTitle></CardHeader>
        <CardContent>
          {chartData.length < 1 ? (
            <p className="text-muted-foreground text-sm py-8 text-center">Nessun dato ancora. Registra il primo peso.</p>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} domain={["auto", "auto"]} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))" }} />
                  <Line type="monotone" dataKey="peso" stroke="hsl(var(--chart-1))" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><BellRing className="h-5 w-5 text-accent" /> Promemoria settimanale</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">Ricordami di pesarmi</div>
              <p className="text-sm text-muted-foreground">Ricevi un'email quando è ora della pesata.</p>
            </div>
            <Switch checked={rem.reminder_enabled} onCheckedChange={(v) => setRem({ ...rem, reminder_enabled: v })} data-testid="reminder-enabled-switch" />
          </div>
          <div className="grid grid-cols-2 gap-3" style={{ opacity: rem.reminder_enabled ? 1 : 0.5 }}>
            <div className="space-y-2">
              <Label>Giorno</Label>
              <Select value={String(rem.reminder_weekday)} onValueChange={(v) => setRem({ ...rem, reminder_weekday: parseInt(v, 10) })} disabled={!rem.reminder_enabled}>
                <SelectTrigger data-testid="reminder-day-select"><SelectValue /></SelectTrigger>
                <SelectContent>{WEEKDAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Ora</Label>
              <Select value={String(rem.reminder_hour)} onValueChange={(v) => setRem({ ...rem, reminder_hour: parseInt(v, 10) })} disabled={!rem.reminder_enabled}>
                <SelectTrigger data-testid="reminder-hour-select"><SelectValue /></SelectTrigger>
                <SelectContent>{[...Array(24)].map((_, h) => <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={saveRem} disabled={savingRem} data-testid="save-reminder-button">
            <Save className="h-4 w-4 mr-2" /> {savingRem ? "Salvataggio…" : "Salva promemoria"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
